// Helpers for base64 conversions
export function base64ToBytes(b64: string): Uint8Array {
  // Handle URL-safe base64 if needed
  const normalized = b64.replace(/-/g, "+").replace(/_/g, "/");
  const binString = atob(normalized);
  const len = binString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binString.charCodeAt(i);
  }
  return bytes;
}

export function bytesToBase64(arr: Uint8Array): string {
  let binString = "";
  for (let i = 0; i < arr.length; i++) {
    binString += String.fromCharCode(arr[i]);
  }
  return btoa(binString);
}

// Derive a CryptoKey from raw bytes
async function importAesKey(keyBytes: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    keyBytes as BufferSource,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"]
  );
}

/**
 * Generates a cryptographically secure 256-bit symmetric key.
 */
export function generateRandomBytes(length = 32): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * Encrypts a payload string or bytes using an AES-GCM key derived from exportKey.
 * Returns a base64 string combining the 12-byte IV and ciphertext.
 */
export async function encryptWithKey(
  plaintext: Uint8Array,
  keyBytes: Uint8Array
): Promise<string> {
  const aesKey = await importAesKey(keyBytes);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    aesKey,
    plaintext as BufferSource
  );

  const ciphertext = new Uint8Array(ciphertextBuffer);
  const result = new Uint8Array(iv.length + ciphertext.length);
  result.set(iv, 0);
  result.set(ciphertext, iv.length);

  return bytesToBase64(result);
}

/**
 * Decrypts a combined IV+ciphertext base64 payload using the provided key bytes.
 */
export async function decryptWithKey(
  combinedBase64: string,
  keyBytes: Uint8Array
): Promise<Uint8Array> {
  const aesKey = await importAesKey(keyBytes);
  const combined = base64ToBytes(combinedBase64);

  if (combined.length < 12) {
    throw new Error("Invalid cipher size");
  }

  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    aesKey,
    ciphertext as BufferSource
  );

  return new Uint8Array(decryptedBuffer);
}

async function readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer();
  }
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Encrypts a File or Blob with AES-GCM-256 using raw file key bytes.
 * Returns a new Blob containing the prepended 12-byte IV followed by ciphertext.
 */
export async function encryptFilePayload(
  file: Blob,
  fileKeyBytes: Uint8Array
): Promise<Blob> {
  const aesKey = await importAesKey(fileKeyBytes);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const fileData = new Uint8Array(await readBlobAsArrayBuffer(file));

  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    aesKey,
    fileData as BufferSource
  );

  const ciphertext = new Uint8Array(ciphertextBuffer);
  const ivBlob = new Blob([iv]);
  const ciphertextBlob = new Blob([ciphertext]);

  return new Blob([ivBlob, ciphertextBlob], {
    type: "application/octet-stream",
  });
}

/**
 * Decrypts an encrypted file Blob using raw file key bytes.
 * Expects the 12-byte IV prepended to the ciphertext.
 */
export async function decryptFilePayload(
  encryptedBlob: Blob,
  fileKeyBytes: Uint8Array,
  originalMimeType?: string
): Promise<Blob> {
  const aesKey = await importAesKey(fileKeyBytes);
  const fileData = new Uint8Array(await readBlobAsArrayBuffer(encryptedBlob));

  if (fileData.length < 12) {
    throw new Error("Invalid encrypted file data");
  }

  const iv = fileData.slice(0, 12);
  const ciphertext = fileData.slice(12);

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    aesKey,
    ciphertext as BufferSource
  );

  return new Blob([decryptedBuffer], {
    type: originalMimeType || "application/octet-stream",
  });
}
